const API_BASE_URL = '/api/documentation/jobs/';

// Helper to get auth headers
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

// Helper function to get current user ID
const getCurrentUserId = () => {
    try {
        // Try to get user ID from different possible storage keys
        const userId = localStorage.getItem('kk_user_id') || 
                      localStorage.getItem('user_id') || 
                      localStorage.getItem('userId');
        
        if (userId) {
            return parseInt(userId);
        }
        
        // Try to parse user data if it's stored as JSON
        const userDataString = localStorage.getItem('kk_user') || localStorage.getItem('user');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                if (userData.id) {
                    return parseInt(userData.id);
                }
                if (userData.user_id) {
                    return parseInt(userData.user_id);
                }
            } catch (err) {
                console.warn('Could not parse user data:', err);
            }
        }
        
        console.warn('Could not find user ID in localStorage');
        return null;
    } catch (error) {
        console.error('Error getting user ID:', error);
        return null;
    }
};

/**
 * Send email and save to database
 * @param {Object} emailData - Email data from the modal
 * @param {number} jobId - Job ID to associate the email with
 * @returns {Promise<Object>} Response from the server
 */
export const sendAndSaveEmail = async (emailData, jobId) => {
    try {
        console.log('sendAndSaveEmail called with:', { emailData, jobId });
        
        const formData = new FormData();
        
        // Get current user ID
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User ID not found. Please log in again.');
        }
        
        // Add email data as JSON string (as expected by the backend)
        const emailPayload = {
            subject: emailData.subject,
            body: emailData.body,
            from_email: emailData.from,
            to_email: emailData.to,
            cc_email: emailData.cc || '',
            email_date: emailData.date,
            sent_by: userId  // Use user ID instead of username
        };
        
        formData.append('email_data', JSON.stringify(emailPayload));
        
        // Add file attachments
        if (emailData.attachments && emailData.attachments.length > 0) {
            emailData.attachments.forEach((file, index) => {
                formData.append(`attachment_${index}`, file);
            });
        }
        
        // Add selected job attachments (by ID) as expected by backend
        if (emailData.selectedJobAttachments && emailData.selectedJobAttachments.length > 0) {
            const jobAttachmentIds = emailData.selectedJobAttachments.map(att => att.id);
            formData.append('job_attachment_ids', JSON.stringify(jobAttachmentIds));
        }
        
        console.log('Sending to endpoint:', `${API_BASE_URL}${jobId}/send-email/`);
        console.log('Email payload:', emailPayload);
        
        const response = await fetch(`${API_BASE_URL}${jobId}/send-email/`, {
            method: 'POST',
            headers: getAuthHeaders(true), // FormData, so don't set Content-Type
            body: formData
        });
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
            const errorData = await response.text();
            console.error('Response error:', errorData);
            throw new Error(`Failed to send email: ${response.status} - ${errorData}`);
        }
        
        const result = await response.json();
        console.log('Email sent successfully:', result);
        
        // Return success response in the expected format
        return {
            success: result.success || true,
            message: result.message || 'Email sent and saved successfully',
            data: result
        };
        
    } catch (error) {
        console.error('Email service error:', error);
        
        // Return error response in expected format
        return {
            success: false,
            message: error.message || 'Failed to send email',
            error: error
        };
    }
};

/**
 * Save email to database without sending
 * @param {Object} emailData - Email data from the modal
 * @param {number} jobId - Job ID to associate the email with
 * @returns {Promise<Object>} Response from the server
 */
export const saveEmailOnly = async (emailData, jobId) => {
    try {
        // Get current user ID
        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User ID not found. Please log in again.');
        }
        
        const emailPayload = {
            emails: [{
                subject: emailData.subject,
                body: emailData.body,
                from_email: emailData.from,
                to_email: emailData.to,
                cc_email: emailData.cc || '',
                email_date: emailData.date,
                sent_by: userId  // Use user ID instead of username
            }]
        };
        
        const response = await fetch(`${API_BASE_URL}${jobId}/emails/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(emailPayload)
        });
        
        if (!response.ok) {
            const errorData = await response.text();
            throw new Error(`Failed to save email: ${errorData}`);
        }
        
        return await response.json();
    } catch (error) {
        console.error('Email save error:', error);
        throw error;
    }
};